/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  FormCancelButton,
  Inputs,
  useApiClient,
  InputPhone,
  TimezoneSelector,
  useFetch,
} from '@marageti/z4-lib';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Traveler, TravelerTags } from '@marageti/z4-sdk/lib/people';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import { useNavigate } from 'react-router-dom';
import { TimeZone, Phone } from '@marageti/z4-sdk/lib/standards';
import { validatePhone } from '../../utils/helpers/validation';
import PartnerSelector from '../partner-lookup-input';

interface TagOption {
  title: string;
  value: string;
}

interface FormValues {
  givenName: string;
  familyName: string;
  phone: Phone;
  timeZone: TimeZone;
  tags: TagOption[];
  referringPartner: Partner | undefined;
}

interface EditTravelerFormProps {
  open: boolean;
  onClose: () => void;
  traveler: Traveler;
  referringPartner: Partner | undefined;
  onSubmitCallback?: (data: FormValues | undefined) => void;
}

const tagOptions: TagOption[] = [
  {
    title: 'Repeat',
    value: 'REPEAT',
  },
  {
    title: 'VIP',
    value: TravelerTags.VIP,
  },
  {
    title: 'FOBY',
    value: TravelerTags.FOBY,
  },
  {
    title: 'Red Flag',
    value: TravelerTags.RED_FLAG,
  },
  {
    title: 'Bad Apple',
    value: TravelerTags.BAD_APPLE,
  },
  {
    title: 'Travel Agent',
    value: TravelerTags.TRAVEL_AGENT,
  },
  {
    title: 'Junk',
    value: TravelerTags.JUNK,
  },
];

const getTravelerTagsAsOptions = (traveler: Traveler): TagOption[] => {
  const isRepeat = traveler.totalZicassoTrips > 1;
  return tagOptions.filter((tagOption) => {
    if (tagOption.value === 'REPEAT') return isRepeat;
    return traveler.tags?.includes(tagOption.value as TravelerTags);
  });
};

export const EditTravelerForm = ({
  open,
  onClose,
  traveler,
  referringPartner,
  onSubmitCallback,
}: EditTravelerFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [submissionError, setSubmissionError] = React.useState<ApiError>();
  const { mutate: mutateTraveler } = useFetch<Traveler>(apiClient.peopleClient.getTraveler, [traveler.id], !!traveler.id);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      givenName: traveler.givenName,
      familyName: traveler.familyName,
      phone: traveler.phone,
      timeZone: traveler.timeZone,
      tags: getTravelerTagsAsOptions(traveler),
      referringPartner,
    },
  });

  const handleClose = () => {
    reset();
    setSubmissionError(undefined);
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const updatedTraveler: Traveler = {
        ...traveler,
        givenName: data.givenName,
        familyName: data.familyName,
        phone: data.phone,
        timeZone: data.timeZone,
        tags: data.tags
          .map((tag) => tag.value)
          .filter((tag): tag is TravelerTags => tag !== 'REPEAT' && Object.values(TravelerTags).includes(tag as TravelerTags)),
      };

      // Add referringPartnerId only if a partner is selected
      if (data.referringPartner?.id) {
        updatedTraveler.referringPartnerId = data.referringPartner.id;
      }

      await apiClient.peopleClient.putTraveler(traveler.id, updatedTraveler);
      enqueueSnackbar('Traveler profile updated.', { variant: 'success' });
      if (onSubmitCallback) onSubmitCallback(data);
      mutateTraveler();
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.code === 401) navigate('/logout');
      setSubmissionError(apiError);
      enqueueSnackbar('Failed to update traveler', { variant: 'default' });
    }
  };

  return (
    <FormDialog open={open}>
      <FormDialogTitle handleClose={handleClose}>Edit Traveler Profile</FormDialogTitle>
      <FormElement name="traveler-editor-form" onSubmit={handleSubmit(onSubmit)}>
        <FormDialogContent>
          <FormInputsStack>
            {submissionError && (
              <FormSubmissionError
                message={submissionError.message}
                errorCode={submissionError.code}
                errorId={submissionError.id}
              />
            )}
            <Controller
              control={control}
              name="givenName"
              rules={{ required: 'Please enter a first name' }}
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="given-name"
                  error={!!errors?.givenName}
                  helperText={errors?.givenName?.message}
                  label="First Name"
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="familyName"
              rules={{ required: 'Please enter a last name' }}
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="family-name"
                  error={!!errors?.familyName}
                  helperText={errors?.familyName?.message}
                  label="Last Name"
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="phone"
              rules={{ validate: validatePhone }}
              render={({ field }) => (
                <InputPhone
                  id="phone"
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.phone}
                  helperText={errors.phone ? 'Please enter a valid phone number' : ''}
                  label="Phone Number"
                />
              )}
            />
            <Controller
              control={control}
              name="timeZone"
              rules={{ required: 'Please select a time zone' }}
              render={({ field }) => (
                <TimezoneSelector
                  id="time-zone"
                  value={field.value}
                  onChange={field.onChange}
                  label="Timezone"
                  error={!!errors.timeZone}
                  helperText={errors.timeZone?.message}
                />
              )}
            />
            <Autocomplete
              multiple
              options={tagOptions}
              disableClearable
              getOptionLabel={(option) => option.title}
              getOptionDisabled={(option) => option.value === 'REPEAT'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                <Chip
                  label={option.title}
                  {...getTagProps({ index })}
                  color="secondary"
                  onDelete={option.value === 'REPEAT' ? undefined : getTagProps({ index }).onDelete}
                />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                />
              )}
              value={watch('tags')}
              onChange={(_: any, selectedOptions: any) => {
                setValue('tags', selectedOptions, {
                  shouldValidate: true,
                });
              }}
            />
            <PartnerSelector
              value={watch('referringPartner')}
              onChange={(_: any, selectedPartner: Partner) => {
                setValue('referringPartner', selectedPartner, {
                  shouldValidate: true,
                });
              }}
              inputProps={{
                label: 'PRL - Lifetime',
                placeholder: 'Search for a partner',
                helperText: 'This will make the traveler a lifetime PRL of the selected partner',
              }}
            />
          </FormInputsStack>
        </FormDialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={handleClose} />
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={!isValid}
          >
            Save
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </FormDialog>
  );
};

export default EditTravelerForm;
